import { FC, Fragment, useState } from "react";
import {
    Box, Divider, FormControl, IconButton, InputAdornment,
    InputLabel, OutlinedInput, Typography, Grid,
    Alert,
    AlertTitle
} from "@mui/material";
import UserInformation from "../../types/UserInformation";
import CheckAttr from "../../utils/CheckAttr";
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from "@mui/lab";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AlertType } from "../../types/AlertType";
import { useNavigate } from "react-router-dom";
import { SnackBarAlert } from "../element/SnackBarAlert";
import { useDispatch } from "react-redux";
import { loggingUser } from "../../redux/actions/logginUser";
import Cookies from "universal-cookie";
import axios, { AxiosInstance } from "axios";
import { profilToAccessCommissionCommercialGRC } from "../../constants/Constants";

interface PasswordFormProps {
    user: UserInformation
}

interface PasswordType {
    password?: string;
    confirm_password?: string;
}

const warningMessage = `Le mot de passe doit contenir au moins un caractère majusculen, 
                        un caractère minuscule, un chiffre, un caractère spécial, 
                        et minimum dix caractères. !`;

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const PasswordForm: FC<PasswordFormProps> = ({ user }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isClickSave, setIsClickSave] = useState<boolean>(false);
    const [checkPassword, setCheckPassword] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [openSnack, setOpenSnack] = useState<boolean>(false);

    const [alertMessage, setAlerteMessage] = useState<string>('');
    const [alertType, setAlertType] = useState<AlertType>("success");

    const [passewordToEdit, setPassewordToEdit] = useState<PasswordType>({
        password: "",
        confirm_password: "",
    });

    const cookies = new Cookies();
    const token: string = cookies.get("jwt");

    const http: AxiosInstance = axios.create({
        baseURL: urlBaseApi,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleChangePassToEdit = (e: any) => {
        const { name, value } = e.target;

        setPassewordToEdit({
            ...passewordToEdit,
            [name]: value
        })
    };


    const isValidatePasseword = (): boolean => {
        setCheckPassword(false);
        if (!CheckAttr(passewordToEdit?.password) || !CheckAttr(passewordToEdit?.confirm_password)) {
            setCheckPassword(true);
            setAlerteMessage("Merci de remplir les champs en rouge SVP!");
            setAlertType("warning");
            setOpenSnack(true);
            return false;
        } else {
            if (passewordToEdit?.password != passewordToEdit?.confirm_password) {
                setCheckPassword(true);
                setAlerteMessage("Le mot de passe et le mot de passe confirmation doit être le même !");
                setAlertType("warning");
                setOpenSnack(true);
                return false;
            } else {
                if (
                    !passewordToEdit?.password!.match(/[0-9]/g) ||
                    !passewordToEdit?.password!.match(/[A-Z]/g) ||
                    !passewordToEdit?.password!.match(/[a-z]/g) ||
                    !passewordToEdit?.password!.match(/[^a-zA-Z\d]/g) ||
                    passewordToEdit?.password!.length < 8
                ) {
                    setCheckPassword(true);
                    setAlerteMessage(warningMessage);
                    setAlertType("warning");
                    setOpenSnack(true);
                    return false;
                }
            }
        }

        return true;
    };

    const handleUpdatePasseword = () => {
        if (isValidatePasseword()) {
            setIsClickSave(true);
            http.put(`user/edit-password/${user?.id}`, passewordToEdit)
                .then(() => {
                    setAlerteMessage("Mis à jour mot de passe efféctuée !");
                    setAlertType("success");
                    setOpenSnack(true);
                    setPassewordToEdit({
                        ...passewordToEdit,
                        password: '',
                        confirm_password: ''
                    });

                    const updatedUser: UserInformation = { ...user, updated_password: true };

                    dispatch(loggingUser(updatedUser));

                    setTimeout(() => {
                        profilToAccessCommissionCommercialGRC.includes(updatedUser?.profils?.libelle)
                            ? navigate("/commissioncomdrc") : navigate("/acceuil");
                    }, 3000);
                }).catch(error => {
                    if (error?.response?.data?.code && (String(error?.response?.data?.code) === "PASSWORD_ALREADY_USED")) {
                        setAlerteMessage(error?.response?.data?.message as string);
                    }

                    setAlertType("error");
                    setOpenSnack(true);
                }).finally(() => {
                    setIsClickSave(false);
                });
        }
    };

    const handleCloseAlert = () => {
        setOpenSnack(false);
    }

    const stackAlert: JSX.Element = SnackBarAlert(openSnack, handleCloseAlert, alertType, alertMessage);

    return (
        <Fragment>
            <Typography variant="h6" gutterBottom color="#FC8404">
                Modification du mot de passe
                <Divider sx={{ mb: 4 }} />
            </Typography>

            {user?.password_expired ?
                <Alert severity="error" sx={{ mb: 3 }}>Votre mot de passe n’est plus valide.
                    Conformément à la politique de sécurité, les mots de passe doivent être changés au moins tous les six mois.
                    Veuillez définir un nouveau mot de passe pour poursuivre votre connexion.</Alert> : null}

            <Grid container spacing={3} columns={{ xs: 6, sm: 6, md: 12 }}>
                <Grid item xs={6} sm={6} md={6}>
                    <FormControl variant="outlined" sx={{ width: "100%" }}>
                        <InputLabel htmlFor="outlined-adornment-password" color="warning">Nouveau mot de passe</InputLabel>
                        <OutlinedInput
                            id="outlined-adornment-password"
                            name="password"
                            color="warning"
                            fullWidth
                            type={showPassword ? 'text' : 'password'}
                            value={passewordToEdit?.password}
                            error={checkPassword}
                            onChange={handleChangePassToEdit}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword(!showPassword)}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            }
                            label="Nouveau mot de passe"
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={6} md={6}>
                    <FormControl variant="outlined" sx={{ width: "100%" }}>
                        <InputLabel htmlFor="outlined-adornment-password" color="warning">Confirmation du nouveau mot de passe</InputLabel>
                        <OutlinedInput
                            id="outlined-adornment-password"
                            name="confirm_password"
                            color="warning"
                            fullWidth
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={passewordToEdit?.confirm_password}
                            error={checkPassword}
                            onChange={handleChangePassToEdit}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            }
                            label="Confirmation du nouveau mot de passe"
                        />
                    </FormControl>
                </Grid>
            </Grid>

            <Alert severity="warning" sx={{ mt: 4 }}>
                <AlertTitle>A noter que:</AlertTitle>
                {warningMessage}
            </Alert>

            <Box display="flex" justifyContent="center" mt={2}>
                <LoadingButton
                    onClick={() => handleUpdatePasseword()}
                    endIcon={<SaveIcon />}
                    loading={isClickSave}
                    loadingPosition="end"
                    sx={{ textTransform: "none" }}
                    variant="contained"
                    color="warning"
                >
                    Mettre à jour le mot de passe
                </LoadingButton>
            </Box>
            {stackAlert}
        </Fragment>
    );
}

export default PasswordForm